import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import FactoryIcon from "@mui/icons-material/Factory";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import VerifiedIcon from "@mui/icons-material/Verified";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";

import fpCommonService from "@plannel/services/fp/fp-common-service";
import fpSimulationCompareService from "@plannel/services/fp/fp-simulation-compare-service";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import KpiWeightConfigPopup from "@plannel/pages/factory-plan/common/popup/KpiWeightConfigPopup";
import { MdBubble } from "@plannel/components/agent/AgentChatMarkdown";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import SplitterContainer from "@plannel/components/SplitterContainer";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ColorUtil from "@plannel/pages/utils/color-util";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";

const { snackBarMessageColor } = ColorUtil;

// ── 상수 ───────────────────────────────────────────────────────────────

// old ScenarioComparePanel.jsx의 kpiDefs 1:1(순서·format·highlight·lowerIsBetter 그대로).
// ⚠️ AVG_LT.lowerIsBetter가 old에서 false로 들어가 있다(리드타임은 짧을수록 좋으므로 의미상 오류).
//    highlight:false라 실제 화면에는 색이 붙지 않아 차이가 없으므로 old 그대로 이관한다.
//    AI 점수 쪽 가중치(DEFAULT_KPI_WEIGHTS.AVG_LT.higherIsBetter)는 반대로 올바르게 들어가 있다.
const KPI_DEFS = [
  { field: "FULFILL_RATE", label: "FP_PLAN_FULFILLMENT_RATE", Icon: EventAvailableIcon, format: "percent", highlight: true, lowerIsBetter: false },
  { field: "ONTIME_RATE", label: "FP_ONTIME_PLAN_RATE", Icon: ScheduleIcon, format: "percent", highlight: true, lowerIsBetter: false },
  { field: "RES_UTIL", label: "FP_AVG_RESRC_UTILIZATION", Icon: PrecisionManufacturingIcon, format: "percent", highlight: true, lowerIsBetter: false },
  // ⚠️ 단위 정정(2026-08-25) — old는 이 값에 FP_HOUR("시간")를 붙였지만 실제로는 **일(day)** 값이다.
  //    `SimulationCompareService.getLeadTime`이 `Duration.between(start,end).toHours() / 24`로
  //    계산한다(변수명만 hoursDiff). old 표기가 틀렸던 것이라 new는 FP_DAY로 바로잡았다.
  { field: "AVG_LT", label: "FP_AVG_PRDT_LEAD_TIME", Icon: FactoryIcon, format: "time-day", highlight: false, lowerIsBetter: false },
  { field: "JC_TM", label: "TIME", Icon: null, format: "time-min", highlight: false, lowerIsBetter: true },
  { field: "JC_CNT", label: "TIMES", Icon: null, format: "number", highlight: false, lowerIsBetter: true }
];

// AI 추천 점수의 지표별 기본 가중치(합 1.0) — old SimulationCompare.jsx:44-51과 동일.
const DEFAULT_KPI_WEIGHTS = [
  { field: "FULFILL_RATE", label: "FP_PLAN_FULFILLMENT_RATE", weight: 0.2, higherIsBetter: true },
  { field: "ONTIME_RATE", label: "FP_ONTIME_PLAN_RATE", weight: 0.2, higherIsBetter: true },
  { field: "RES_UTIL", label: "FP_AVG_RESRC_UTILIZATION", weight: 0.2, higherIsBetter: true },
  { field: "AVG_LT", label: "FP_AVG_PRDT_LEAD_TIME", weight: 0.2, higherIsBetter: false },
  // 작업 교체 2행은 라벨이 같으므로 단위 키를 덧붙여 구분한다 — old는 여기서 미리 번역한 합성
  //   문자열(`${FP_JOB_CHANGE} (${TIME})`)을 넘겼는데(SimulationCompare.jsx:49-50), 그러면 언어를
  //   바꿔도 팝업 라벨이 안 바뀐다. 키를 그대로 들고 있다가 팝업이 렌더 시점에 합성한다.
  { field: "JC_TM", label: "FP_JOB_CHANGE", unitLabel: "TIME", weight: 0.1, higherIsBetter: false },
  { field: "JC_CNT", label: "FP_JOB_CHANGE", unitLabel: "TIMES", weight: 0.1, higherIsBetter: false }
];
const INITIAL_WEIGHT_MAP = Object.fromEntries(DEFAULT_KPI_WEIGHTS.map(kw => [kw.field, kw.weight]));

// 최대 비교 버전 수 — old VersionGroupInputField의 `value?.length === 5` 가드와 동일.
// 백엔드 getJobChange도 labels.size() < 5로 5건까지만 채운다.
const MAX_COMPARE_VERSIONS = 5;

// 테이블 레이아웃(old ScenarioComparePanel의 고정 px 그대로 — sticky 오프셋 계산에 쓰인다).
const KPI_LABEL_WIDTH = 220;
const JC_LABEL_WIDTH = 110;
const VERSION_COL_MIN_WIDTH = 300;
const PANEL_HEADER_HEIGHT = 44;

// KPI 의미색 — 구조(테두리/배경/헤더)는 MUI 테마를 쓰고, "좋다/나쁘다/추천/확정"의 의미색만
// old 값을 상수로 유지한다(dashboard/FpVersusPerformance.js의 CELL_RED/BLUE/GREEN과 같은 방식).
const C_GOOD = "#10b981";
const C_BAD = "#ef4444";
const C_MID = "#f59e0b";
const C_AI = "#137fec";
const C_CONFIRM = "#22c55e";
const C_HOVER = "#475569";

// AI 분석 로딩 중 순환 문구(3.5초 간격).
// old는 7종이었으나 첫 문구 `PREPARING_INSIGHT`("T³Insight AI 분석 준비 중...")는 **제외**했다 —
// plannel에는 T³Insight가 없어 제품명이 화면에 노출되면 안 된다(2026-08-25 사용자 지시).
const AI_LOADING_MESSAGE_KEYS = [
  "FP_MSG_DELIVERING_FACTORY_PLAN_DATA",
  "ANALYZING_DATA",
  "FINDING_OPTIMAL_SCENARIO",
  "GENERATING_ANALYSIS_RESULTS",
  "GENERATING_ANALYSIS_REPORT",
  "WAITING_PLEASE"
];
const AI_LOADING_STEP_MS = 3500;
const AI_MOCK_ELAPSED_MS = AI_LOADING_STEP_MS * 2;

// 딥링크 대상(§13-1 sender) — TabMenuList의 reduxKey이자 그 화면의 viewName prop.
const KPI_DASHBOARD_VIEW = "FP_SIMULATION_KPI_DASHBOARD";

// AI 추천 / 확정 계획 뱃지 공통 sx — Chip은 아이콘 색을 자기 palette로 덮으므로 슬롯을 직접 지정한다.
const badgeSx = {
  height: 20, fontSize: 11, fontWeight: 700, color: "#fff",
  "& .MuiChip-icon": { color: "#fff" }
};

const isConfirmed = (version) => version?.confirmYn === true || version?.confirmYn === "Y";
const toNumber = (value) => (value === null || value === undefined ? Number.NaN : parseFloat(value));

// ── 로컬 컴포넌트: 버전 다중선택 ────────────────────────────────────────

/**
 * old `VersionGroupInputField.jsx` 대응. 메인버전 그룹 헤더 + 체크박스 목록.
 * 선택 제약 2개(old 동일):
 *  1) 최대 5개
 *  2) 첫 선택 버전과 mainVersionCd / stepCd / stepSeq가 모두 같은 버전만 선택 가능
 * 닫힌 입력칸에는 첫 선택 버전 코드 + (2개 이상이면) ⊕n-1 을 보여준다.
 */
const VersionGroupSelect = ({ t, groups, value, onChange }) => {
  const flatVersions = useMemo(() => Object.values(groups ?? {}).flat(), [groups]);
  const findVersion = (versionCd) => flatVersions.find(v => v.versionCd === versionCd);

  const toggle = (versionCd) => {
    if (value.includes(versionCd)) {
      onChange(value.filter(cd => cd !== versionCd));
    } else {
      onChange([...value, versionCd]);
    }
  };

  const isDisabled = (version) => {
    const anchor = findVersion(value[0]);
    if (anchor) {
      const sameGroup = anchor.mainVersionCd === version.mainVersionCd
        && anchor.stepCd === version.stepCd
        && String(anchor.stepSeq) === String(version.stepSeq);
      if (!sameGroup) return true;
    }
    return value.length >= MAX_COMPARE_VERSIONS && !value.includes(version.versionCd);
  };

  const renderValue = (selected) => {
    const first = findVersion(selected?.[0]);
    if (!first) return "";
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <Typography noWrap component="span" sx={{ fontSize: "inherit" }}>{first.versionCd}</Typography>
        {selected.length > 1 && (
          <>
            <AddCircleIcon fontSize="small" />
            <Typography component="span" sx={{ fontSize: "inherit" }}>{selected.length - 1}</Typography>
          </>
        )}
      </Box>
    );
  };

  return (
    <FormControl size="small" sx={{ ml: 1.5, width: 260 }}>
      <InputLabel>{t("SIMULATION_VERSION")}</InputLabel>
      <Select
        multiple
        label={t("SIMULATION_VERSION")}
        value={value}
        renderValue={renderValue}
        MenuProps={{ sx: { maxHeight: 400 } }}
        // 항목 클릭을 MenuItem onClick에서 처리하므로 Select 자체의 onChange는 쓰지 않는다
        // (체크박스와 행 클릭이 각각 발화해 두 번 토글되는 것을 막는다).
        onChange={() => {}}
      >
        {Object.keys(groups ?? {}).flatMap(groupLabel => ([
          // 그룹 라벨은 서버(SimulationCompareService.getVersions)가 "<메인버전코드> : <설명>"으로
          // 조립해 Map 키로 내려준 값 그대로다 — 화면에서 다시 만들지 않는다.
          <ListSubheader key={`group-${groupLabel}`} sx={{ fontWeight: 700, lineHeight: "36px", borderBottom: 1, borderColor: "divider" }}>
            {groupLabel}
          </ListSubheader>,
          ...groups[groupLabel].map(version => (
            <MenuItem
              key={version.versionCd}
              value={version.versionCd}
              disabled={isDisabled(version)}
              onClick={() => toggle(version.versionCd)}
            >
              <ListItemIcon>
                <Checkbox size="small" checked={value.includes(version.versionCd)} tabIndex={-1} disableRipple />
              </ListItemIcon>
              <ListItemText primary={`${version.versionCd} : ${version.descTxt ?? ""}`} />
            </MenuItem>
          ))
        ]))}
      </Select>
    </FormControl>
  );
};

// ── 로컬 컴포넌트: KPI 비교 매트릭스 ────────────────────────────────────

/**
 * old `ScenarioComparePanel.jsx` 대응. 행 = KPI 6종 + AI 점수, 열 = 선택 버전(최대 5).
 * 값 강조는 highlight 컬럼에서만, 그리고 최대≠최소일 때만 붙는다(old 동일).
 */
const ScenarioComparePanel = ({ t, versions, data, scoreMap, aiPickVerCds, onOpenWeightConfig, onNavigate, onConfirm }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const kpiStats = useMemo(() => {
    const stats = {};
    KPI_DEFS.forEach(kpi => {
      const values = data.map(row => toNumber(row[kpi.field])).filter(v => !Number.isNaN(v));
      stats[kpi.field] = {
        max: values.length > 0 ? Math.max(...values) : null,
        min: values.length > 0 ? Math.min(...values) : null
      };
    });
    return stats;
  }, [data]);

  // 표시값 사전 포맷팅 — hover 때마다 toLocaleString이 반복되지 않게(old 동일한 이유).
  const displayValues = useMemo(() => data.map(row => {
    const formatted = {};
    KPI_DEFS.forEach(kpi => {
      const raw = row[kpi.field];
      if (raw === null || raw === undefined) {
        formatted[kpi.field] = "-";
        return;
      }
      const num = toNumber(raw);
      const text = Number.isNaN(num) ? String(raw) : num.toLocaleString();
      if (kpi.format === "percent") formatted[kpi.field] = `${text}%`;
      else if (kpi.format === "time-day") formatted[kpi.field] = `${text}${t("FP_DAY")}`;
      else if (kpi.format === "time-min") formatted[kpi.field] = `${text}${t("FP_MINUTES")}`;
      else formatted[kpi.field] = text;
    });
    return formatted;
  }), [data, t]);

  // 이벤트 위임 — 셀마다 핸들러를 다는 대신 컨테이너 하나에서 data-index를 읽는다(old 동일).
  const handleMouseOver = useCallback((event) => {
    const cell = event.target.closest("td, th");
    const next = cell?.hasAttribute("data-index") ? parseInt(cell.getAttribute("data-index"), 10) : null;
    setHoveredIdx(prev => (prev === next ? prev : next));
  }, []);

  // 값 텍스트/점수 막대에 쓰는 강조색 — AI 추천/확정일 때만 붙고 hover는 관여하지 않는다(old 동일).
  const accentOf = (row) => {
    if (aiPickVerCds.has(row?.versionCd)) return C_AI;
    if (isConfirmed(row)) return C_CONFIRM;
    return null;
  };

  // 열 강조 테두리/배경 색. 우선순위 AI추천 > 확정 > hover (old와 동일).
  const columnColor = (row, index) => accentOf(row) ?? (hoveredIdx === index ? C_HOVER : null);

  const columnBg = (row, index, strength) => {
    const color = columnColor(row, index);
    return color ? alpha(color, strength) : "transparent";
  };

  const sideBorderSx = (color, { top, bottom }) => (color ? {
    content: '""', position: "absolute", top, left: 0, right: 0, bottom,
    borderLeft: `2px solid ${color}`, borderRight: `2px solid ${color}`,
    pointerEvents: "none", zIndex: 3
  } : {});

  const hasScores = scoreMap.size > 0;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
      <Box sx={{
        height: PANEL_HEADER_HEIGHT, flex: "0 0 auto", px: 2, borderBottom: 1, borderColor: "divider",
        bgcolor: "grey.50", display: "flex", alignItems: "center", justifyContent: "space-between"
      }}>
        <Typography variant="subtitle2" component="h2" sx={{ display: "flex", alignItems: "center", gap: 0.75, fontWeight: 700, color: "text.secondary" }}>
          <AnalyticsIcon sx={{ fontSize: 20, color: C_AI }} />
          {t("SIMUL_RESULT_COMPARE")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button size="small" endIcon={<TuneIcon />} onClick={onOpenWeightConfig}>
            {t("AI_RECOMMENDATION_KPI_WEIGHT_CONFIG")}
          </Button>
          <Button size="small" endIcon={<OpenInNewIcon />} onClick={onNavigate} disabled={data.length === 0}>
            {t("menuFpSimulationKpiDashboard")}
          </Button>
        </Box>
      </Box>

      <TableContainer sx={{ flex: 1, minHeight: 0 }} onMouseOver={handleMouseOver} onMouseLeave={() => setHoveredIdx(null)}>
        <Table
          size="small"
          sx={{
            tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0,
            minWidth: versions.length > 0 ? KPI_LABEL_WIDTH + versions.length * VERSION_COL_MIN_WIDTH : "auto"
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={2}
                sx={{
                  width: KPI_LABEL_WIDTH, minWidth: KPI_LABEL_WIDTH, maxWidth: KPI_LABEL_WIDTH,
                  position: "sticky", left: 0, zIndex: 4, bgcolor: "grey.50",
                  fontWeight: 700, color: "text.secondary", textTransform: "uppercase"
                }}
              >
                {t("KPI")}
              </TableCell>
              {versions.map((version, index) => {
                const color = columnColor(version, index);
                const aiPick = aiPickVerCds.has(version.versionCd);
                const confirmed = isConfirmed(version);
                return (
                  <TableCell
                    key={version.versionCd}
                    data-index={index}
                    sx={{
                      width: `calc((100% - ${KPI_LABEL_WIDTH}px) / ${versions.length})`,
                      bgcolor: columnBg(version, index, 0.04),
                      position: "relative", overflow: "visible", verticalAlign: "top",
                      "&::after": color ? {
                        content: '""', position: "absolute", top: 0, left: 0, right: 0, bottom: -1,
                        borderTop: `2px solid ${color}`, borderLeft: `2px solid ${color}`, borderRight: `2px solid ${color}`,
                        pointerEvents: "none", zIndex: 3
                      } : {}
                    }}
                  >
                    {(aiPick || confirmed) && (
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 0.75 }}>
                        {aiPick && (
                          <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                            label={t("AI_RECOMMENDATION")}
                            sx={{ ...badgeSx, bgcolor: C_AI }} />
                        )}
                        {confirmed && (
                          <Chip size="small" icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
                            label={t("CONFIRMED_PLAN")}
                            sx={{ ...badgeSx, bgcolor: C_CONFIRM }} />
                        )}
                      </Box>
                    )}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{version.versionCd}</Typography>
                    <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>{version.policyNm}</Typography>
                    <Typography variant="caption" color="text.secondary">{version.descTxt}</Typography>
                    {hoveredIdx === index && (
                      <Button
                        size="small"
                        endIcon={<CheckCircleIcon />}
                        onClick={() => onConfirm(version)}
                        sx={{ position: "absolute", bottom: 4, right: 4, fontWeight: 700, whiteSpace: "nowrap", color: accentOf(version) ?? undefined }}
                      >
                        {t("PLAN_CONFIRM")}
                      </Button>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {KPI_DEFS.map((kpi) => {
              const { max: maxVal, min: minVal } = kpiStats[kpi.field] ?? { max: null, min: null };
              const isJobChange = kpi.field.startsWith("JC_");
              return (
                <TableRow key={kpi.field}>
                  {/* 작업 교체 2행(JC_TM/JC_CNT)은 old와 동일하게 rowSpan=2 묶음 라벨을 앞에 둔다. */}
                  {kpi.field === "JC_TM" && (
                    <TableCell rowSpan={2} sx={{ position: "sticky", left: 0, zIndex: 4, bgcolor: "grey.50", width: JC_LABEL_WIDTH }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PublishedWithChangesIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                        <Box component="span" sx={{ fontSize: 13, fontWeight: 700 }}>{t("FP_JOB_CHANGE")}</Box>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell
                    colSpan={isJobChange ? 1 : 2}
                    sx={{ position: "sticky", left: isJobChange ? JC_LABEL_WIDTH : 0, zIndex: 4, bgcolor: "grey.50" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {kpi.Icon && <kpi.Icon sx={{ fontSize: 18, color: "text.disabled" }} />}
                      <Box component="span" sx={{ fontSize: 13, fontWeight: 700 }}>{t(kpi.label)}</Box>
                    </Box>
                  </TableCell>
                  {data.map((row, index) => {
                    const num = toNumber(row[kpi.field]);
                    const spread = maxVal !== minVal;
                    const isMax = !Number.isNaN(num) && num === maxVal && spread;
                    const isMin = !Number.isNaN(num) && num === minVal && spread;
                    const isMid = !isMax && !isMin && !Number.isNaN(num) && spread;
                    const isGood = kpi.highlight && (kpi.lowerIsBetter ? isMin : isMax);
                    const isBad = kpi.highlight && (kpi.lowerIsBetter ? isMax : isMin);
                    const accent = accentOf(row);
                    const color = columnColor(row, index);
                    const valueColor = isGood ? C_GOOD
                      : isBad ? C_BAD
                        : (kpi.highlight && isMid && accent) ? accent
                          : "text.primary";
                    return (
                      <TableCell
                        key={row.versionCd}
                        data-index={index}
                        sx={{
                          bgcolor: columnBg(row, index, 0.02), position: "relative", overflow: "visible",
                          "&::after": sideBorderSx(color, { top: -1, bottom: -2 })
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontSize: 14, color: valueColor,
                            fontWeight: kpi.highlight && (isMax || isMin || (isMid && accent)) ? 700 : 500
                          }}
                        >
                          {displayValues[index]?.[kpi.field] ?? "-"}
                        </Box>
                        {kpi.highlight && kpi.format === "percent" && !Number.isNaN(num) && (
                          <Box sx={{ mt: 0.75, height: isMax || isMin ? 6 : 4, bgcolor: "grey.200", borderRadius: 999, overflow: "hidden" }}>
                            <Box sx={{
                              height: "100%", borderRadius: 999,
                              width: `${Math.min(100, Math.max(0, num))}%`,
                              bgcolor: isGood ? C_GOOD : isBad ? C_BAD : isMid ? C_MID : "grey.400"
                            }} />
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}

            {hasScores && (
              <TableRow>
                <TableCell colSpan={2} sx={{ position: "sticky", left: 0, zIndex: 4, bgcolor: "grey.100", borderTop: 2, borderColor: "divider" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 18, color: C_AI }} />
                    <Box component="span" sx={{ fontSize: 13, fontWeight: 700, color: C_AI }}>{t("AI_SCORE")}</Box>
                  </Box>
                </TableCell>
                {data.map((row, index) => {
                  const score = scoreMap.get(row.versionCd);
                  const color = columnColor(row, index);
                  const accent = accentOf(row);
                  return (
                    <TableCell
                      key={row.versionCd}
                      data-index={index}
                      sx={{
                        bgcolor: columnBg(row, index, 0.04), position: "relative", overflow: "visible",
                        borderTop: 2, borderColor: "divider",
                        "&::after": color ? {
                          content: '""', position: "absolute", top: -1, left: 0, right: 0, bottom: 0,
                          borderLeft: `2px solid ${color}`, borderRight: `2px solid ${color}`, borderBottom: `2px solid ${color}`,
                          pointerEvents: "none", zIndex: 3
                        } : {}
                      }}
                    >
                      {score !== undefined && (
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                            <Box component="span" sx={{ fontSize: 15, fontWeight: 800, color: accent ?? "text.primary" }}>
                              {score.toFixed(1)}
                            </Box>
                            <Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: "text.disabled" }}>/ 100</Box>
                          </Box>
                          <Box sx={{ mt: 0.5, height: 4, bgcolor: "grey.200", borderRadius: 999, overflow: "hidden" }}>
                            <Box sx={{ height: "100%", width: `${score}%`, borderRadius: 999, bgcolor: accent ?? "grey.400" }} />
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ── 로컬 컴포넌트: AI 분석 패널 (프레임 + 목업) ─────────────────────────

// TODO(MIG-T3): [simulationcompare/SimulationCompare] AI 분석 패널의 LLM 연동 미이관.
//   old: SimulationAiPanel이 `POST insight/engine/service_invoke/FactoryPlanComparisonService`로
//        T³Insight LLM을 호출하고(input_data = PLAN_SCOPE/VERSION_CODES/LANGUAGE_CODE/KPI_WEIGHTS/DATA),
//        parseInsightRestApiResponse로 푼 마크다운을 LlmMarkdown으로 렌더링했다.
//   사유: `@insight`는 이관 제외 확정 alias이고 plannel에 T³Insight가 없다
//        (PlanProblemDashboard.js의 "Insight는 saas 안에서 AI로 새로 개발 예정" 판정과 동일).
//        2026-08-25 사용자 협의: **패널 프레임은 그대로 만들고 리포트 본문은 목업**으로 채운다.
//        실제 연동 시점에는 `services/ai/scenarioService.js` 계열(saas-ai-agent FastAPI)로 배선하고
//        아래 buildMockReport / MOCK 지연을 제거하면 된다.
const AiAnalysisPanel = ({ t, report, loading, loadingMessageIdx, canRun, onRun }) => (
  <Box sx={{ height: "100%", display: "flex", flexDirection: "column", border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
    <Box sx={{
      height: PANEL_HEADER_HEIGHT, flex: "0 0 auto", px: 2, borderBottom: 1, borderColor: "divider",
      bgcolor: "grey.50", display: "flex", alignItems: "center", justifyContent: "space-between"
    }}>
      <Typography variant="subtitle2" component="h2" sx={{ display: "flex", alignItems: "center", gap: 0.75, fontWeight: 700, color: "text.secondary" }}>
        {/* old 헤더 키 AI_ASSISTANT("AI 분석 인사이트"/"AI Insights Assistant")는 제품명 T³Insight를
            연상시키는 문구라 쓰지 않는다(2026-08-25 사용자 지시) — 중립 라벨 키로 대체. */}
        <AutoAwesomeIcon sx={{ fontSize: 18, color: C_AI }} />
        {t("FP_AI_ANALYSIS")}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Button size="small" endIcon={<RefreshIcon />} disabled={loading || !canRun} onClick={onRun}>
          {t("AI_EXEC_ANALYZE")}
        </Button>
        {loading && <Chip size="small" label={t("AI_ANALYZING")} sx={{ height: 24, fontWeight: 700, color: C_AI, bgcolor: alpha(C_AI, 0.08) }} />}
        {!loading && report && <Chip size="small" label={t("AI_ANALYSIS_COMPLETE")} sx={{ height: 24, fontWeight: 700, color: C_GOOD, bgcolor: alpha(C_GOOD, 0.08) }} />}
      </Box>
    </Box>
    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2.5, py: 2 }}>
      {loading && (
        <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "text.disabled", fontSize: 18 }}>
          {t(AI_LOADING_MESSAGE_KEYS[loadingMessageIdx])}
        </Box>
      )}
      {/* 분석 전 안내문 — old 키 INSIGHT_FP_REPORT_MSG는 키 이름에 제품명이 박혀 있어
          중립 키(FP_MSG_AI_ANALYSIS_READY)로 대체했다. 표시 문구의 의미는 동일하다. */}
      {!loading && (report
        ? <MdBubble text={report} />
        : (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "text.disabled", fontSize: 18 }}>
            {t("FP_MSG_AI_ANALYSIS_READY")}
          </Box>
        ))}
    </Box>
  </Box>
);

// ── 화면 ───────────────────────────────────────────────────────────────

const SimulationCompare = ({ t }) => {
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  const [planScope, setPlanScope] = useState("");
  const [planningDate, setPlanningDate] = useState(null);
  const [dateWithVersions, setDateWithVersions] = useState([]);
  const [versionGroups, setVersionGroups] = useState({});
  const [versionCds, setVersionCds] = useState([]);

  const [selectedVersions, setSelectedVersions] = useState([]);
  const [compareData, setCompareData] = useState([]);
  const [kpiWeights, setKpiWeights] = useState(DEFAULT_KPI_WEIGHTS);
  const [weightPopupOpen, setWeightPopupOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [progressOn, setProgressOn] = useState(false);
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [aiReport, setAiReport] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMessageIdx, setAiMessageIdx] = useState(0);

  // 요청 토큰 가드 2개(§0-8) — 버전 캐스케이드(계획범위/계획수립일)와 KPI 조회는 따로 뒤집힌다.
  const latestVersionReqRef = useRef(0);
  const latestDataReqRef = useRef(0);
  // 버전 목록 flat 캐시 — 조회 시 versionCds → 버전 객체 매핑에 쓴다(old 모듈 전역 searchVersionOptions 대응).
  const flatVersionsRef = useRef([]);
  const confirmTargetRef = useRef(null);
  const aiTimerRef = useRef(null);

  const showSnack = (severity, content) => setSnackInfo({ open: true, severity, content });

  // ── 조회 ─────────────────────────────────────────────────────────────

  // 진행 중인 목업 분석을 중단한다. 조회로 데이터가 바뀌면 이전 실행이 끝나면서 낡은 리포트를
  // 덮어쓰는 것을 막는다(리포트는 클릭 시점의 compareData로 조립되므로).
  const stopAiAnalysis = () => {
    if (aiTimerRef.current) {
      clearInterval(aiTimerRef.current);
      aiTimerRef.current = null;
    }
    setAiLoading(false);
  };

  const clearData = () => {
    setSelectedVersions([]);
    setCompareData([]);
    stopAiAnalysis();
    setAiReport(null);
  };

  const loadData = useCallback((overrides) => {
    const scope = overrides?.planScope ?? planScope;
    const cds = overrides?.versionCds ?? versionCds;
    const flat = overrides?.flatVersions ?? flatVersionsRef.current;

    if (!scope || !cds?.length) {
      showSnack(snackBarMessageColor.warning, t("MSG_5114"));
      return;
    }

    // 응답 배열은 백엔드 getSortedVersionCds()의 versionSeq 내림차순이므로 화면도 같게 정렬해
    // 인덱스로 맞춘다(old 동일). versionSeq는 axios-bigint 때문에 BigInt라 Number로 비교한다.
    const versions = flat
      .filter(version => cds.includes(version.versionCd))
      .sort((a, b) => Number(b.versionSeq) - Number(a.versionSeq));

    const reqId = ++latestDataReqRef.current;
    const params = { planScope: scope, versionCds: cds };
    setLoading(true);

    // KPI 5종은 서로 독립이라 allSettled로 부분 실패를 허용한다 — 하나가 500이어도 나머지 지표는
    // 그대로 보여주고 실패한 지표만 "-"로 남는다(old는 Promise 하나가 깨지면 화면 전체가 비었다).
    Promise.allSettled([
      fpSimulationCompareService.getFulfillRate(params),
      fpSimulationCompareService.getOnTimeRate(params),
      fpSimulationCompareService.getLeadTime(params),
      fpSimulationCompareService.getResourceUtilization(params),
      fpSimulationCompareService.getJobChange(params)
    ])
      .then(results => {
        if (reqId !== latestDataReqRef.current) return;

        results.filter(result => result.status === "rejected").forEach(result => debug(result.reason));
        const body = (index) => results[index]?.value?.data;

        const fulfillData = body(0)?.data ?? [];
        const onTimeData = body(1)?.data ?? [];
        const leadTimeData = body(2)?.data ?? [];
        const resourceUtilData = body(3)?.data ?? [];
        const jcTmData = body(4)?.jcTmSumData ?? [];
        const jcCntData = body(4)?.jcCntSumData ?? [];

        setSelectedVersions(versions);
        setCompareData(versions.map((version, index) => ({
          ...version,
          FULFILL_RATE: fulfillData[index],
          ONTIME_RATE: onTimeData[index],
          RES_UTIL: resourceUtilData[index],
          AVG_LT: leadTimeData[index],
          JC_TM: jcTmData[index],
          JC_CNT: jcCntData[index]
        })));
        // 조회할 때마다 이전 분석 결과를 지운다(old aiPanelRef.current?.clear()).
        stopAiAnalysis();
        setAiReport(null);

        if (results.some(result => result.status === "rejected")) {
          showSnack(snackBarMessageColor.warning, t("MSG_NoData"));
        }
      })
      .finally(() => {
        if (reqId === latestDataReqRef.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope, versionCds, t]);

  /**
   * 버전 목록 조회.
   * - `resetSelection`(old `isInitial`) — 선택을 기본값(첫 그룹 앞 5개)으로 되돌린다. 계획범위/
   *   계획수립일이 바뀐 경우다. **확정 후 재조회에서는 꺼야 한다** — old도 `loadVersionList()`를
   *   인자 없이 불러 사용자가 고른 버전 선택을 그대로 유지했다.
   * - `autoLoad` — 목록을 읽은 뒤 KPI까지 이어서 조회한다.
   *
   * 버전 체크박스 변경만으로는 재조회하지 않는다(old도 전역 툴바 조회 버튼을 눌러야 했다) —
   * 버튼 위치만 `FilterContainer` 마지막 자식 슬롯으로 옮겼다.
   */
  const loadVersionList = useCallback((scope, date, { resetSelection = true, autoLoad = false } = {}) => {
    if (!scope || !date) {
      setVersionGroups({});
      setVersionCds([]);
      flatVersionsRef.current = [];
      clearData();
      return;
    }

    const reqId = ++latestVersionReqRef.current;

    fpSimulationCompareService.getVersions({ planScope: scope, planningDate: dateUtils.formatDate(date, "yyyyMMdd") })
      .then(response => {
        if (reqId !== latestVersionReqRef.current) return;

        const groups = response.data ?? {};
        const groupLabels = Object.keys(groups);
        flatVersionsRef.current = Object.values(groups).flat();
        setVersionGroups(groups);

        if (groupLabels.length === 0) {
          setVersionCds([]);
          clearData();
          return;
        }

        // 선택 유지 경로: versionCds를 넘기지 않으면 loadData가 현재 선택을 그대로 쓴다.
        if (!resetSelection) {
          if (autoLoad) loadData({ planScope: scope, flatVersions: flatVersionsRef.current });
          return;
        }

        const initialCds = (groups[groupLabels[0]] ?? [])
          .map(version => version.versionCd)
          .slice(0, MAX_COMPARE_VERSIONS);
        setVersionCds(initialCds);

        if (autoLoad) loadData({ planScope: scope, versionCds: initialCds, flatVersions: flatVersionsRef.current });
      })
      .catch(e => {
        if (reqId !== latestVersionReqRef.current) return;
        debug(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData]);

  // 1단계 — 계획범위 변경 → 버전이 존재하는 계획수립일 목록 → 첫 날짜 선택.
  // 날짜가 바뀌면 아래 2단계 effect가 버전 목록을 다시 읽는다.
  useEffect(() => {
    if (!planScope) return;

    let cancelled = false;
    fpCommonService.getPlanDts({ planScope })
      .then(response => {
        if (cancelled) return;
        // ZDate(moment 기반)를 쓴다 — 네이티브 Date는 "yyyy-MM-dd"를 UTC 자정으로 파싱해
        // 음수 오프셋 지역에서 하루 밀린다(VersionSearchGroup.js와 동일 처리).
        const dates = (response.data ?? []).map(value => new ZDate(value));
        setDateWithVersions(dates);
        setPlanningDate(dates[0] ?? null);
      })
      .catch(e => debug(e));

    return () => { cancelled = true; };
  }, [planScope]);

  // 2단계 — 계획범위/계획수립일이 확정되면 버전 목록 조회 + 기본 선택으로 자동조회.
  useEffect(() => {
    loadVersionList(planScope, planningDate, { resetSelection: true, autoLoad: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope, planningDate]);

  // ── 계획 확정 ────────────────────────────────────────────────────────

  const openConfirmDialog = (version) => {
    confirmTargetRef.current = version;
    setDialogInfo({ open: true, title: "confirm", content: t("FP_MSG_SIMULATION_CONFIRM") });
  };

  const handleDialog = (action) => {
    if (action !== "CONFIRM") return;

    const version = confirmTargetRef.current;
    confirmTargetRef.current = null;
    if (!version) return;

    // confirmedBy는 사용자 id가 아니라 이름 문자열이라 화면이 로그인 사용자명을 같이 보낸다.
    const storedUser = localStorage.getItem("user");
    let userNm = "";
    try {
      userNm = JSON.parse(storedUser)?.username ?? "";
    } catch (e) {
      debug(e);
    }

    setProgressOn(true);
    fpSimulationCompareService.confirmVersion({ planScope, versionCd: version.versionCd, userNm })
      .then(response => {
        showSnack(snackBarMessageColor.confirm, t(response.data?.message ?? "FP_MSG_SIMULATION_CONFIRMED"));
        // 확정 여부가 바뀌었으니 버전 목록과 데이터를 모두 다시 읽는다(old 동일).
        // resetSelection:false — 사용자가 고른 비교 대상은 그대로 두고 confirmYn만 갱신한다.
        loadVersionList(planScope, planningDate, { resetSelection: false, autoLoad: true });
      })
      .catch(e => {
        debug(e);
        showSnack(snackBarMessageColor.saveFail, t(e?.response?.data?.message ?? "FP_MSG_FAIL_SIMULATION_CONFIRMED"));
      })
      .finally(() => setProgressOn(false));
  };

  // ── AI 점수(가중합) ──────────────────────────────────────────────────

  // old calcScenarioScores 이식 — 지표별 min-max 정규화 후 가중합(0~100). 최고점이 AI 추천.
  const { scoreMap, aiPickVerCds } = useMemo(() => {
    if (compareData.length === 0) return { scoreMap: new Map(), aiPickVerCds: new Set() };

    const ranges = {};
    kpiWeights.forEach(kw => {
      const values = compareData.map(row => toNumber(row[kw.field])).filter(v => !Number.isNaN(v));
      ranges[kw.field] = {
        min: values.length > 0 ? Math.min(...values) : 0,
        max: values.length > 0 ? Math.max(...values) : 0
      };
    });

    const scores = compareData.map(row => {
      let total = 0;
      kpiWeights.forEach(kw => {
        const value = toNumber(row[kw.field]);
        const { min, max } = ranges[kw.field];
        let normalized;
        if (Number.isNaN(value)) normalized = 0;
        else if (max === min) normalized = 100;
        else if (kw.higherIsBetter) normalized = ((value - min) / (max - min)) * 100;
        else normalized = ((max - value) / (max - min)) * 100;
        total += normalized * kw.weight;
      });
      return { versionCd: row.versionCd, score: total };
    });

    const best = Math.max(...scores.map(s => s.score));
    return {
      scoreMap: new Map(scores.map(s => [s.versionCd, s.score])),
      aiPickVerCds: new Set(scores.filter(s => s.score === best).map(s => s.versionCd))
    };
  }, [compareData, kpiWeights]);

  // ── AI 분석 (목업) ───────────────────────────────────────────────────

  /**
   * 목업 리포트 — LLM 대신 **이 화면이 방금 조회한 실제 KPI 값**으로 마크다운을 조립한다.
   * 없는 사실을 지어내지 않도록 추천 버전 / KPI 표 / 지표별 최고·최저만 서술한다.
   */
  const buildMockReport = useCallback(() => {
    if (compareData.length === 0) return "";

    const pick = compareData.find(row => aiPickVerCds.has(row.versionCd)) ?? compareData[0];
    const pickScore = scoreMap.get(pick.versionCd) ?? 0;

    const formatValue = (kpi, row) => {
      const raw = row[kpi.field];
      if (raw === null || raw === undefined) return "-";
      const num = toNumber(raw);
      const text = Number.isNaN(num) ? String(raw) : num.toLocaleString();
      if (kpi.format === "percent") return `${text}%`;
      if (kpi.format === "time-day") return `${text}${t("FP_DAY")}`;
      if (kpi.format === "time-min") return `${text}${t("FP_MINUTES")}`;
      return text;
    };
    const kpiLabel = (kpi) => (kpi.field.startsWith("JC_") ? `${t("FP_JOB_CHANGE")} (${t(kpi.label)})` : t(kpi.label));

    const headerRow = `| ${t("KPI")} | ${compareData.map(row => row.versionCd).join(" | ")} |`;
    const separator = `| --- | ${compareData.map(() => "---").join(" | ")} |`;
    const bodyRows = KPI_DEFS.map(kpi => `| ${kpiLabel(kpi)} | ${compareData.map(row => formatValue(kpi, row)).join(" | ")} |`);

    // 지표별 최고/최저는 highlight 지표(비율 3종)에서만 의미가 있어 그쪽만 서술한다.
    const bullets = KPI_DEFS.filter(kpi => kpi.highlight).flatMap(kpi => {
      const scored = compareData
        .map(row => ({ row, value: toNumber(row[kpi.field]) }))
        .filter(item => !Number.isNaN(item.value));
      if (scored.length < 2) return [];

      const best = scored.reduce((a, b) => (b.value > a.value ? b : a));
      const worst = scored.reduce((a, b) => (b.value < a.value ? b : a));
      if (best.row.versionCd === worst.row.versionCd) return [];

      return [
        `- ${t("FP_MSG_AI_REPORT_BEST", { kpi: t(kpi.label), version: best.row.versionCd, value: formatValue(kpi, best.row) })}`,
        `- ${t("FP_MSG_AI_REPORT_WORST", { kpi: t(kpi.label), version: worst.row.versionCd, value: formatValue(kpi, worst.row) })}`
      ];
    });

    return [
      `**${t("AI_RECOMMENDATION")} : ${pick.versionCd}**`,
      "",
      t("FP_MSG_AI_REPORT_PICK", { version: pick.versionCd, score: pickScore.toFixed(1) }),
      "",
      headerRow,
      separator,
      ...bodyRows,
      "",
      ...bullets,
      "",
      t("FP_MSG_AI_REPORT_CONCLUSION", { version: pick.versionCd })
    ].join("\n");
  }, [compareData, aiPickVerCds, scoreMap, t]);

  const runAiAnalysis = () => {
    if (compareData.length === 0) return;

    stopAiAnalysis();
    setAiReport(null);
    setAiMessageIdx(0);
    setAiLoading(true);

    const report = buildMockReport();
    const startedAt = Date.now();

    // 로딩 문구 순환(old와 동일한 3.5초 간격). 마지막 문구에서 멈춘다.
    aiTimerRef.current = setInterval(() => {
      setAiMessageIdx(prev => Math.min(prev + 1, AI_LOADING_MESSAGE_KEYS.length - 1));
      if (Date.now() - startedAt < AI_MOCK_ELAPSED_MS) return;
      clearInterval(aiTimerRef.current);
      aiTimerRef.current = null;
      setAiLoading(false);
      setAiReport(report);
    }, AI_LOADING_STEP_MS);
  };

  useEffect(() => () => {
    if (aiTimerRef.current) clearInterval(aiTimerRef.current);
  }, []);

  // ── 딥링크 (§13-1 sender) ────────────────────────────────────────────

  // old linkTo — 확정 버전이 있으면 그것, 없으면 AI 추천 버전으로 계획 결과 요약 화면을 연다.
  // 반드시 updateViewState → updateHistoryState 순서(§13-1).
  // 계획 결과 요약이 권한 밖이면 아래 onNavigate 를 주지 않아 이동 버튼이 뜨지 않는다.
  const openKpiDashboard = () => {
    const target = compareData.find(row => isConfirmed(row))
      ?? compareData.find(row => aiPickVerCds.has(row.versionCd));
    if (!target) return;

    const navFields = {
      planScope,
      planningDate: target.planDt ? dateUtils.formatDate(target.planDt, "yyyy-MM-dd") : null,
      versionCd: target.versionCd,
      plantCd: (target.plants ?? []).map(plant => plant.plantCd)
    };
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(KPI_DASHBOARD_VIEW, navFields)));
    reduxDispatch(reduxUtil.updateHistoryState(KPI_DASHBOARD_VIEW));
  };

  // ── 렌더 ─────────────────────────────────────────────────────────────

  // TODO(MIG-T3): [simulationcompare/SimulationCompare] AUTO_LOAD 뷰옵션 분기 미이관.
  //   old: getViewOptions(activeViewId,'AUTO_LOAD')가 true일 때만 계획범위 확정 후 자동조회.
  //   사유: new에는 화면별 뷰 옵션 레지스트리라는 개념 자체가 없다(fp-simulation-screen-guide.md §B-11
  //        의 NO_SEARCHAREA와 동일 계열). 항상 자동조회한다.
  // TODO(MIG-T2): [simulationcompare/SimulationCompare] 다른 화면 → 이 화면 딥링크 수신 미이관.
  //   old: useEffect([location])에서 location.state.planScope를 받아 PlanScope를 세팅.
  //   사유: old 전체에서 UI_FP_SIMUL_COMPARE로 history.push하는 코드가 0건이라(insight 모듈의
  //        기능 매핑 상수에만 이름이 등장) 실제로 신호를 보내는 화면이 없는 죽은 경로다.
  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t("FP_LABEL_PLANNING_DATE")}
            format="yyyy-MM-dd"
            value={planningDate}
            // old는 highlightDates로 강조만 했지만, 공용 VersionSearchGroup이 이미 "버전 없는 날짜는
            // 선택 자체를 막는" 방식으로 확립돼 있어 new 선례를 따른다(버전 0건 상태가 생기지 않는다).
            shouldDisableDate={(date) => !dateWithVersions.some(d => dateUtils.formatDate(d, "yyyy-MM-dd") === dateUtils.formatDate(date, "yyyy-MM-dd"))}
            onAccept={(date) => setPlanningDate(date)}
            slotProps={{ textField: { size: "small", sx: { ml: 1.5, width: 190 } } }}
          />
        </LocalizationProvider>
        <VersionGroupSelect t={t} groups={versionGroups} value={versionCds} onChange={setVersionCds} />
        <Box sx={{ display: "flex" }}>
          <Button variant="outlined" size="small" startIcon={<SearchIcon />} onClick={() => loadData()}>
            {t("search")}
          </Button>
        </Box>
      </FilterContainer>

      <SplitterContainer initialSizes={[63, 37]}>
        <Box sx={{ height: "calc(100% - 8px)", pb: 1, minHeight: 0 }}>
          <ScenarioComparePanel
            t={t}
            versions={selectedVersions}
            data={compareData}
            scoreMap={scoreMap}
            aiPickVerCds={aiPickVerCds}
            onOpenWeightConfig={() => setWeightPopupOpen(true)}
            onNavigate={isPermitted(KPI_DASHBOARD_VIEW) ? openKpiDashboard : undefined}
            onConfirm={openConfirmDialog}
          />
        </Box>
        <Box sx={{ height: "calc(100% - 8px)", mt: 1, minHeight: 0 }}>
          <AiAnalysisPanel
            t={t}
            report={aiReport}
            loading={aiLoading}
            loadingMessageIdx={aiMessageIdx}
            canRun={compareData.length > 0}
            onRun={runAiAnalysis}
          />
        </Box>
      </SplitterContainer>

      <KpiWeightConfigPopup
        open={weightPopupOpen}
        kpiWeights={kpiWeights}
        initialWeights={INITIAL_WEIGHT_MAP}
        onClose={() => setWeightPopupOpen(false)}
        onSubmit={(weights, validationMessage) => {
          if (validationMessage) {
            showSnack(snackBarMessageColor.inputError, validationMessage);
            return;
          }
          setKpiWeights(prev => prev.map(kw => ({ ...kw, weight: weights[kw.field] ?? kw.weight })));
          setWeightPopupOpen(false);
        }}
      />

      <Dialog
        open={dialogInfo.open}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      {/* 이 화면엔 그리드가 없어 §0-8b의 그리드 로딩 오버레이를 쓸 수 없다 — 조회/확정 모두 Backdrop. */}
      <Backdrop open={loading || progressOn} sx={{ zIndex: theme => theme.zIndex.drawer + 1, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default withTranslation()(SimulationCompare);
